import posthog from 'posthog-js';
import { POSTHOG_API_KEY, POSTHOG_API_HOST } from './env';
import { captureMarketingContext } from './marketing';

const CONSENT_COOKIE = 'ALL3DConsentPreferences';
const CONSENT_DAYS = 150;

export const defaultPreferences = {
  analytics: false,
  advertising: false,
  sales: false,
};

export function readConsent() {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + CONSENT_COOKIE + '=([^;]*)')
    );
    return match ? JSON.parse(decodeURIComponent(match[1])) : null;
  } catch {
    return null;
  }
}

export function writeConsent(prefs) {
  const d = new Date();
  d.setTime(d.getTime() + CONSENT_DAYS * 24 * 60 * 60 * 1000);
  const parts = [
    `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(prefs))}`,
    `expires=${d.toUTCString()}`,
    'path=/',
    'SameSite=Strict',
  ];
  // Only set an explicit domain in production — browsers reject cookies whose
  // domain doesn't match the current host, so localhost would silently drop the
  // write and show the banner on every refresh.
  if (window.location.hostname !== 'localhost') {
    parts.push('domain=all3d.ai');
  }
  document.cookie = parts.join(';');
}

function loadPostHog() {
  if (window._posthogLoaded) return;
  try {
    captureMarketingContext();
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_API_HOST,
      capture_pageview: true,
      capture_exceptions: true,
    });
    // Set flag synchronously — posthog-js queues capture/register/identify
    // internally until init's async load finishes, so callers don't need to
    // wait. Gating on a deferred flag here would silently drop early events.
    window._posthogLoaded = true;
    window.posthog = posthog;
    captureMarketingContext();
  } catch {}
}

function loadFacebookPixel() {
  if (window._fbPixelLoaded) return;
  try {
    // eslint-disable-next-line
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', '312796056603999');
    window.fbq('track', 'PageView');
    window._fbPixelLoaded = true;
  } catch {}
}

function loadGTM() {
  if (window._gtmLoaded) return;
  try {
    // eslint-disable-next-line
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-W7NZ3JT');
    const ns = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = 'https://www.googletagmanager.com/ns.html?id=GTM-W7NZ3JT';
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.cssText = 'display:none;visibility:hidden';
    ns.appendChild(iframe);
    document.body.insertBefore(ns, document.body.firstChild);
    window._gtmLoaded = true;
  } catch {}
}

function loadSamplead() {
  if (window._sampleadLoaded) return;
  try {
    const script = document.createElement('script');
    script.src = 'https://pixel.sampleadai.net/a63f0286-7a8b-4a28-b76c-c612d213cf60';
    script.async = true;
    document.head.appendChild(script);
    window._sampleadLoaded = true;
  } catch {}
}

export function applyConsent(prefs) {
  if (prefs.analytics) {
    loadPostHog();
  }
  if (prefs.advertising) {
    loadFacebookPixel();
    loadGTM();
  }
  if (prefs.sales) {
    loadSamplead();
  }

  if (typeof window !== 'undefined') {
    let consentEvent;
    if (typeof window.CustomEvent === 'function') {
      consentEvent = new CustomEvent('all3d:consent-updated', {
        detail: { prefs },
      });
    } else {
      consentEvent = document.createEvent('CustomEvent');
      consentEvent.initCustomEvent(
        'all3d:consent-updated',
        false,
        false,
        { prefs }
      );
    }
    window.dispatchEvent(consentEvent);
  }
}

// Apply prior consent at module-load time so PostHog is ready before any
// React component's useEffect runs. Without this, child-component mount
// effects (e.g. `pricing_page_visit`) fire before App's useEffect calls
// applyConsent, so they no-op on the `_posthogLoaded` check and the
// captures are lost. App.js still calls applyConsent in its useEffect —
// that call is idempotent (loadPostHog guards on `_posthogLoaded`).
if (typeof window !== 'undefined') {
  try {
    const existing = readConsent();
    if (existing) applyConsent(existing);
  } catch {}
}
